import { OnboardingLayout } from '@/components/OnboardingLayout'
import { getErrorMessage } from '@/lib/queries'
import { getApi } from '@/stores/api'
import { setSessionToken, setTenant } from '@/stores/session'
import { MESSAGE } from '@repo/constants'
import { Button } from '@repo/ui/components'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CheckCircle2Icon, LoaderCircleIcon, XCircleIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const searchSchema = z.object({
  token: z.string().default(''),
})

export const Route = createFileRoute('/onboard/confirm-join')({
  validateSearch: searchSchema,
  component: ConfirmJoinPage,
})

function ConfirmJoinPage() {
  const { token } = Route.useSearch()
  const { t } = useTranslation('ui')
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const hasAttempted = useRef(false)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setError(t('returningDriver.confirmJoin.error.notFound'))
      return
    }

    // Guard against React StrictMode double-invoke consuming the one-time token.
    if (hasAttempted.current) return
    hasAttempted.current = true

    const run = async () => {
      try {
        const api = getApi()
        const res = await api.public.onboarding['confirm-join'][':token'].$post({
          param: { token },
        })
        const data = await res.json()
        // setSessionToken must land before navigate — the api client reads
        // that atom, and the _auth guard the transition triggers needs the
        // new bearer token already in the store.
        if ('session' in data && data.session) {
          setSessionToken(data.session.sessionToken)
          setTenant(data.session.tenant)
          navigate({
            to: '/carriers/$slug/driver-onboarding/onboarding',
            params: { slug: data.session.tenant.slug },
          })
          return
        }
        setStatus('success')
      } catch (err: unknown) {
        const message = getErrorMessage(err, '')
        // "Already used" means the join happened on a prior click — treat as
        // success so a re-click doesn't render an error.
        if (message === MESSAGE.JOIN_TOKEN_ALREADY_USED) {
          setStatus('success')
          return
        }
        setStatus('error')
        if (message === MESSAGE.JOIN_TOKEN_EXPIRED) {
          setError(t('returningDriver.confirmJoin.error.expired'))
        } else if (message === MESSAGE.JOIN_TOKEN_NOT_FOUND) {
          setError(t('returningDriver.confirmJoin.error.notFound'))
        } else {
          setError(t('returningDriver.confirmJoin.error.generic'))
        }
      }
    }

    run()
  }, [token, t, navigate])

  return (
    <OnboardingLayout>
      <div className="flex flex-col items-center gap-6 py-12 text-center">
        {status === 'loading' && (
          <>
            <LoaderCircleIcon className="size-10 animate-spin text-primary" />
            <p className="text-muted-foreground">{t('returningDriver.confirmJoin.loading')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="rounded-full bg-primary/10 p-4">
              <CheckCircle2Icon className="size-10 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">
                {t('returningDriver.confirmJoin.success.title')}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('returningDriver.confirmJoin.success.subtitle')}
              </p>
            </div>
            <Link to="/login">
              <Button>{t('returningDriver.confirmJoin.success.goToLogin')}</Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="rounded-full bg-destructive/10 p-4">
              <XCircleIcon className="size-10 text-destructive" />
            </div>
            <p className="text-sm text-destructive">{error}</p>
            <Link to="/login">
              <Button variant="outline">
                {t('returningDriver.confirmJoin.success.goToLogin')}
              </Button>
            </Link>
          </>
        )}
      </div>
    </OnboardingLayout>
  )
}
